import { keyframes, styled } from "goober";
import { type ReactNode, useLayoutEffect, useRef, useState } from "react";
import type { IconName } from "./Icon";
import { EASE_OUT, MOTION_FAST_MS } from "./motion";
import { PressButton } from "./PressButton";

export function attachmentFileIcon(mediaType: string): IconName {
  return mediaType === "application/pdf" ? "file-scan" : "paperclip";
}

export const AttachmentPreviewRow = styled("div")`
  display: flex;
  gap: 6px;
  padding: 0 4px 8px;
  flex-wrap: wrap;
`;

export const AttachmentPreview = styled(PressButton)`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 8px 0 6px;
  height: 20px;
  background: var(--alpha4);
  border-radius: 38px;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  min-width: 0;
  max-width: 200px;
  &:hover {
    background: var(--alpha8);
  }
  .file-icon {
    display: flex;
  }
  .remove-icon {
    display: none;
  }
  &:hover .file-icon {
    display: none;
  }
  &:hover .remove-icon {
    display: flex;
  }
`;

// Sent attachments (chat messages, email outputs) aren't removable; clicking
// opens a preview modal, so the trailing icon swaps to an action glyph on hover.
export const AttachmentLabel = styled("button")`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 8px 0 6px;
  height: 20px;
  background: var(--alpha4);
  border: none;
  border-radius: 38px;
  color: var(--text-secondary);
  cursor: pointer;
  width: fit-content;
  max-width: 200px;
  &:hover {
    background: var(--alpha8);
  }
  .file-icon {
    display: flex;
  }
  .download-icon {
    display: none;
  }
  &:hover .file-icon {
    display: none;
  }
  &:hover .download-icon {
    display: flex;
  }
`;

export const marchingDashes = keyframes`
  to { stroke-dashoffset: -10; }
`;

export function AttachmentRowAnimator({
  visible,
  children,
}: {
  visible: boolean;
  children: ReactNode;
}) {
  const [mounted, setMounted] = useState(visible);
  const ref = useRef<HTMLDivElement>(null);
  const heightRef = useRef(0);

  // Latch mounted on so the node stays rendered for its exit animation, which
  // clears it again when the animation finishes below.
  if (visible && !mounted) setMounted(true);

  // Track height while visible, animate wrap/unwrap changes
  useLayoutEffect(() => {
    if (!visible || !mounted || !ref.current) return;
    if (ref.current.getAnimations().length > 0) return;
    const newHeight = ref.current.offsetHeight;
    const oldHeight = heightRef.current;
    if (oldHeight > 0 && oldHeight !== newHeight) {
      ref.current.animate(
        [{ height: `${oldHeight}px` }, { height: `${newHeight}px` }],
        { duration: MOTION_FAST_MS, easing: EASE_OUT }
      );
    }
    heightRef.current = newHeight;
  });

  // Animate enter/exit
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !mounted) return;

    const anim = visible
      ? el.animate([{ height: "0px" }, { height: `${el.offsetHeight}px` }], {
          duration: MOTION_FAST_MS,
          easing: EASE_OUT,
        })
      : el.animate(
          [
            { height: `${heightRef.current}px`, opacity: 1 },
            { height: "0px", opacity: 0 },
          ],
          { duration: MOTION_FAST_MS, easing: EASE_OUT, fill: "forwards" }
        );

    if (!visible) anim.onfinish = () => setMounted(false);

    return () => anim.cancel();
  }, [visible, mounted]);

  if (!mounted) return null;

  return (
    <div ref={ref} style={{ overflow: "hidden" }}>
      {children}
    </div>
  );
}

export const Dropzone = styled("div")`
  position: absolute;
  inset: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  background: var(--alpha4);
  border-radius: 12px;
`;
